import { supabase } from '../../../integrations/supabase/client';
import { Node } from 'reactflow';
import { NodeGroup } from '../types/groupTypes';

export interface TrainingGroup {
  id?: string;
  user_id: string;
  group_id: string;
  flow_id: string | null;
  numero_telefono?: string | null;
  instruction: string | null;
  product_keys: any; // JSONB array de product_keys
  prompt_format: string | null;
  node_order: any[] | any;
  metadata: any;
  created_at?: string;
  updated_at?: string;
}

/**
 * Obtiene el numero_telefono desde agent_channels basándose en el flow_id
 * Esto asegura que el grupo quede vinculado al canal correcto del agente
 */
const getPhoneFromAgentChannel = async (flowId: string | null): Promise<string | null> => {
  if (!flowId) return null;

  // Buscar el agente que tiene este workflow activo
  const { data: agent } = await supabase
    .from('agents')
    .select('id')
    .eq('active_workflow_id', flowId)
    .maybeSingle();

  if (!agent) return null;

  // Obtener el número del canal del agente
  const { data: channel } = await supabase
    .from('agent_channels')
    .select('numero_telefono')
    .eq('agent_id', agent.id)
    .limit(1)
    .maybeSingle();

  return channel?.numero_telefono || null;
};

export const trainingGroupsApi = {
  /**
   * Guarda o actualiza un grupo de entrenamiento
   */
  async upsertGroup(
    group: NodeGroup,
    nodes: Node[],
    flowId: string | null
  ): Promise<TrainingGroup> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuario no autenticado');

    const numeroTelefono = await getPhoneFromAgentChannel(flowId);

    // group_id persistido = NÚMERO secuencial del grupo (1, 2, 3...), NO el id interno.
    // Mantiene consistencia con training_messages.group_id y la automatización externa.
    const dbGroupId = String(group.number);

    // Filtrar solo los nodos que pertenecen a este grupo
    const groupNodes = nodes.filter(node => group.nodeIds.includes(node.id));

    // CRÍTICO: Ordenar por sequenceOrder que ya debe estar actualizado en los nodos
    const sortedNodes = groupNodes.sort((a, b) => {
      const seqA = (a.data as any).sequenceOrder || 999999;
      const seqB = (b.data as any).sequenceOrder || 999999;
      return seqA - seqB;
    });
    
    console.log('🔢 Guardando grupo con sequence_order:', sortedNodes.map(n => ({
      id: n.id,
      type: n.type,
      sequenceOrder: (n.data as any).sequenceOrder
    })));

    // CRÍTICO: Obtener los datos más recientes de la BD para cada nodo
    // Esto evita usar messageId desactualizado y asegura que node_order tenga datos correctos
    const nodeIds = sortedNodes.map(n => n.id);
    let dbMessages: any[] = [];
    
    if (flowId && nodeIds.length > 0) {
      const { data: messages } = await supabase
        .from('training_messages')
        .select('id, node_id, media_url, text_content, ai_description')
        .eq('flow_id', flowId)
        .in('node_id', nodeIds)
        .order('created_at', { ascending: false });
      
      // Crear un mapa de node_id -> mensaje más reciente (para evitar duplicados)
      const messageMap = new Map<string, any>();
      (messages || []).forEach(msg => {
        if (!messageMap.has(msg.node_id)) {
          messageMap.set(msg.node_id, msg);
        }
      });
      dbMessages = Array.from(messageMap.values());
    }

    // Crear un mapa para acceso rápido
    const dbMessageMap = new Map(dbMessages.map(m => [m.node_id, m]));

    // Crear node_order array con la información de cada nodo
    const nodeOrder = sortedNodes.map(node => {
      const data = node.data as any;
      const type = node.type;
      const dbMsg = dbMessageMap.get(node.id);

      // Extraer media_url: primero de BD, luego del nodo
      let mediaUrl = dbMsg?.media_url || null;
      if (!mediaUrl) {
        if (type === 'imageMessage') mediaUrl = data.imageUrl;
        else if (type === 'audioMessage') mediaUrl = data.audioUrl;
        else if (type === 'videoMessage') mediaUrl = data.videoUrl;
        else if (type === 'documentMessage') mediaUrl = data.documentUrl;
      }

      // Extraer text_content: primero de BD, luego del nodo
      let textContent = dbMsg?.text_content || null;
      if (!textContent) {
        if (type === 'textMessage') textContent = data.message;
        else if (type === 'imageMessage' || type === 'videoMessage' || type === 'documentMessage') {
          textContent = data.caption || null;
        }
      }

      // Extraer ai_description: primero de BD, luego del nodo
      let aiDescription = dbMsg?.ai_description || null;
      if (!aiDescription) {
        if (type === 'imageMessage') aiDescription = data.aiDescription;
        else if (type === 'audioMessage') aiDescription = data.aiTranscription;
      }

      return {
        node_id: node.id,
        node_type: type,
        sequence_order: data.sequenceOrder || 1,
        message_id: dbMsg?.id || data.messageId || null,
        media_url: mediaUrl,
        text_content: textContent,
        ai_description: aiDescription,
      };
    });

    // Crear el prompt_format basado en los nodos
    const promptFormat = sortedNodes.map(node => {
      const data = node.data as any;
      const type = node.type;

      if (type === 'textMessage') {
        return `[Mensaje de texto]\n  Contenido: ${data.message || 'Sin contenido'}`;
      } else if (type === 'imageMessage') {
        const parts = ['[Imagen]'];
        // Caption solo si el usuario lo escribió
        if (data.caption && data.caption.trim()) {
          parts.push(`  Caption: ${data.caption}`);
        }
        // Descripción de la IA en campo separado
        if (data.aiDescription) {
          parts.push(`  Descripción de imagen: ${data.aiDescription}`);
        }
        return parts.join('\n');
      } else if (type === 'audioMessage') {
        const parts = ['[Audio]'];
        if (data.aiTranscription) {
          parts.push(`  Transcripción de audio: ${data.aiTranscription}`);
        }
        return parts.join('\n');
      } else if (type === 'videoMessage') {
        return `[Video]${data.caption && data.caption.trim() ? `\n  Caption: ${data.caption}` : ''}`;
      } else if (type === 'documentMessage') {
        return `[Documento]${data.caption && data.caption.trim() ? `\n  Caption: ${data.caption}` : ''}`;
      }
      return '[Mensaje desconocido]';
    }).join('\n\n');

    // Metadata adicional
    const metadata = {
      color: group.color,
      node_count: groupNodes.length,
      lead_status: group.leadStatus || null,
      group_number: group.number,
    };

    const groupData: TrainingGroup = {
      user_id: user.id,
      group_id: dbGroupId,
      flow_id: flowId,
      numero_telefono: numeroTelefono,
      instruction: group.instruction || null,
      product_keys: group.productKeys || [],
      prompt_format: promptFormat,
      node_order: nodeOrder,
      metadata,
    };

    // Verificar si ya existe CON flow_id para evitar sobrescribir grupos de otros flujos
    let query = supabase
      .from('training_groups')
      .select('id')
      .eq('group_id', dbGroupId);

    // Solo agregar filtro de flow_id si existe
    if (flowId) {
      query = query.eq('flow_id', flowId);
    }
    
    const { data: existing } = await query.maybeSingle();

    if (existing) {
      // Actualizar - también filtrar por flow_id
      let updateQuery = supabase
        .from('training_groups')
        .update(groupData)
        .eq('group_id', dbGroupId);

      if (flowId) {
        updateQuery = updateQuery.eq('flow_id', flowId);
      }
      
      const { data, error } = await updateQuery.select().single();

      if (error) throw error;
      return data;
    } else {
      // Insertar
      const { data, error } = await supabase
        .from('training_groups')
        .insert(groupData)
        .select()
        .single();

      if (error) throw error;
      return data;
    }
  },

  /**
   * Obtiene todos los grupos de un flow
   */
  async getGroupsByFlowId(flowId: string): Promise<TrainingGroup[]> {
    const { data, error } = await supabase
      .from('training_groups')
      .select('*')
      .eq('flow_id', flowId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  /**
   * Elimina un grupo por su group_id y flow_id
   * IMPORTANTE: Debe filtrar por flow_id para no eliminar grupos de otros workflows
   */
  async deleteGroup(groupId: string, flowId?: string | null): Promise<void> {
    let query = supabase
      .from('training_groups')
      .delete()
      .eq('group_id', groupId);
    
    // CRÍTICO: Filtrar por flow_id para evitar eliminar grupos de otros workflows
    if (flowId) {
      query = query.eq('flow_id', flowId);
    }

    const { error } = await query;
    if (error) throw error;
  },
};
